/**
 * Which course and lesson are on screen — for GA4 only. The activity-log
 * bridge (activityBridge.ts) adds them to the video events it maps; the log's
 * own records stay exactly as they were (its ctx is not touched).
 *
 * Pages set it with useAnalyticsContext while they are mounted; the newest
 * mounted one wins for each key.
 */
import { useEffect } from "react";

type Ctx = Record<string, string | undefined>;

let nextId = 1;
const stack: Array<{ id: number; data: Ctx }> = [];

/** Adds a context; the returned function removes it again. */
export const pushAnalyticsContext = (data: Ctx): (() => void) => {
    const id = nextId++;
    stack.push({ id, data });
    return () => {
        const i = stack.findIndex((e) => e.id === id);
        if (i >= 0) stack.splice(i, 1);
    };
};

/** The merged context of every mounted page (newest wins), without empty values. */
export const analyticsContext = (): Ctx => {
    const out: Ctx = {};
    for (const { data } of stack) {
        for (const [k, v] of Object.entries(data)) if (v) out[k] = v;
    }
    return out;
};

/** React: `data` is in the analytics context while the component is mounted. */
export const useAnalyticsContext = (data: Ctx): void => {
    const key = JSON.stringify(data);
    useEffect(
        () => pushAnalyticsContext(JSON.parse(key) as Ctx),
        [key],
    );
};

// ---- Chapters of a single-video course ----
//
// One player plays the whole course; a lesson is a chapter (a time range).
// The bridge asks which chapter a position belongs to, so video_progress /
// video_stop name the chapter where the viewer actually was — not the one
// that was on screen when playback started.

export interface AnalyticsChapter {
    lesson_id: string;
    start: number;
    end: number;
    /** Chapter title as the page shows it (video_title in GA4). */
    title?: string;
}

const chapterStack: Array<{ id: number; courseId: string; chapters: AnalyticsChapter[] }> = [];

/** Registers the chapters of `courseId`; the returned function removes them. */
export const pushAnalyticsChapters = (courseId: string, chapters: AnalyticsChapter[]): (() => void) => {
    const id = nextId++;
    chapterStack.push({ id, courseId, chapters });
    return () => {
        const i = chapterStack.findIndex((e) => e.id === id);
        if (i >= 0) chapterStack.splice(i, 1);
    };
};

/**
 * The chapter of `courseId` at `seconds` (newest registration wins). Past the
 * end it is the last chapter, before the first one the first — the same rule
 * the course view uses for its highlighted chapter.
 */
export const analyticsChapterAt = (courseId: unknown, seconds: unknown): AnalyticsChapter | undefined => {
    if (typeof courseId !== "string" || typeof seconds !== "number" || !Number.isFinite(seconds)) return undefined;
    for (let i = chapterStack.length - 1; i >= 0; i--) {
        const { courseId: c, chapters } = chapterStack[i];
        if (c !== courseId || !chapters.length) continue;
        const hit = chapters.find((ch) => seconds >= ch.start && seconds < ch.end);
        if (hit) return hit;
        const last = chapters[chapters.length - 1];
        return seconds >= last.end ? last : chapters[0];
    }
    return undefined;
};

/** React: the course's chapters are known to the bridge while the component is mounted. */
export const useAnalyticsChapters = (courseId: string | undefined, chapters: AnalyticsChapter[]): void => {
    const key = courseId && chapters.length ? JSON.stringify(chapters) : "";
    useEffect(() => {
        if (!courseId || !key) return undefined;
        return pushAnalyticsChapters(courseId, JSON.parse(key) as AnalyticsChapter[]);
    }, [courseId, key]);
};
